import { Component, DestroyRef, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { TutorialModalComponent } from '../../shared/ui/tutorial-modal/tutorial-modal.component';
import { UpgradeModalComponent } from '../../shared/ui/upgrade-modal/upgrade-modal.component';
import { CommandPaletteComponent } from '../../shared/command-palette';
import { CompaniesService } from '../../features/companies/data/companies.service';
import { AuthService } from '../../core/services/authentication/auth-service';
import { CompanyContextService } from '../../core/services/company-context.service';
import { ToastService } from '../../core/services/toast.service';
import { navigateByOnboardingResult, resolveOnboardingRoute } from '../../core/utils/post-login-routing';
import { IntegrationsService } from '../../features/settings/data/integrations.service';
import { NotificationHubService } from '../../core/services/notification-hub.service';
import { CompanyFlagsService } from '../../core/services/company-flags.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, TopbarComponent, TutorialModalComponent, UpgradeModalComponent, CommandPaletteComponent],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  private readonly _integrations = inject(IntegrationsService);
  private readonly _notificationHub = inject(NotificationHubService);
  private readonly _companyFlags = inject(CompanyFlagsService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _transloco = inject(TranslocoService);

  protected readonly tutorialOpen = signal(false);
  protected readonly tutorialState = signal<'form' | 'loading' | 'success'>('form');
  protected readonly integrationRequired = signal(false);
  protected readonly showLaunchModal = signal(false);
  protected readonly currentMsgIndex = signal(0);
  protected readonly launchMessages = [
    'Verilerinizi eksiksiz aktarıyoruz',
    'Entegrasyon bağlantılarınız hazırlanıyor',
    'e-Fatura altyapınız kuruluyor',
    'Sistemimiz 1 Nisan\'da sizlerle',
  ];

  constructor(
    private readonly _authService: AuthService,
    private readonly _companiesService: CompaniesService,
    private readonly _companyContext: CompanyContextService,
    private readonly _router: Router,
    private readonly _toastService: ToastService,
  ) {
    this._destroyRef.onDestroy(() => this._notificationHub.stop());
    this.isLoading.set(true);
    this._companiesService.listForCurrentUser().subscribe({
      next: (userCompanies) => {
        this._companyContext.initializeFromMemberships(userCompanies);
        const user = this._authService.getCurrentUser();
        // Note: isAccountant is intentionally NOT passed here. The accountant exemption only
        // applies to the post-login landing decision (login/callback). Once an accountant
        // deliberately enters the company panel, normal rules apply — with no company they
        // should be sent to onboarding to create one, not bounced back to the accountant panel.
        const result = resolveOnboardingRoute(
          userCompanies,
          this._companyContext.getSelectedCompanyId(),
          user?.publicId ?? '',
        );

        if (result.path !== '/dashboard') {
          navigateByOnboardingResult(result, this._router, this._toastService, this._transloco);
          return;
        }

        this.isLoading.set(false);
        this._notificationHub.start();
        this.checkIntegrationStatus();
      },
      error: () => {
        this._companyContext.clear();
        this._authService.logout();
        this._router.navigate(['/login']);
      },
    });
  }
  protected readonly mobileOpen = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly isSidebarCollapsed = signal(this.readInitialCollapsedState());

  protected toggleSidebarCollapse(): void {
    const next = !this.isSidebarCollapsed();
    this.isSidebarCollapsed.set(next);
    this.persistCollapsedState(next);
  }

  private readInitialCollapsedState(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem('jl-sidebar-collapsed') === 'true';
  }

  private persistCollapsedState(collapsed: boolean): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem('jl-sidebar-collapsed', String(collapsed));
  }

  protected openTutorial(): void {
    this.tutorialState.set('form');
    this.tutorialOpen.set(true);
  }

  protected onTutorialAccept(data: { username: string; password: string }): void {
    this.tutorialState.set('loading');
    this._integrations.create({
      appKey: 'QNB-ESOLUTIONS',
      apiKey: data.username,
      apiSecret: data.password,
      senderAlias: '',
    }).pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: () => {
        this.tutorialState.set('success');
        this.integrationRequired.set(false);
        setTimeout(() => {
          this.tutorialOpen.set(false);
          this.startMessageCycle();
        }, 2500);
      },
      error: () => this.tutorialState.set('form'),
    });
  }

  private checkIntegrationStatus(): void {
    const companyId = this._companyContext.getSelectedCompanyId();
    if (!companyId) {
      this.checkEFaturaIntegration();
      return;
    }
    this._companyFlags.resolveAndStore(companyId).subscribe({
      next: (flags) => {
        // e-Fatura entegrasyonu yalnızca destekleyen (TR) şirketler için gerekli;
        // bayrak çözülemezse mevcut davranışı koru.
        if (flags.supportsEInvoicing === false) return;
        this.checkEFaturaIntegration();
      },
      error: () => this.checkEFaturaIntegration(),
    });
  }

  private checkEFaturaIntegration(): void {
    this._integrations.list().subscribe({
      next: (settings) => {
        if (settings.some((s) => s.appKey === 'QNB-ESOLUTIONS')) {
          this.showLaunchModal.set(true);
          this.startMessageCycle();
        } else {
          this.integrationRequired.set(true);
        }
      },
    });
  }

  private startMessageCycle(): void {
    const interval = setInterval(() => {
      this.currentMsgIndex.set((this.currentMsgIndex() + 1) % this.launchMessages.length);
    }, 3000);
    this._destroyRef.onDestroy(() => clearInterval(interval));
  }
}
